/**
 * Review and publish (BR-107, BR-111, BR-113): the last guide step. A row per step with its saved
 * state and Edit, the listing fee line, and "Continue to payment", which opens the existing,
 * unchanged fee, consent and card dialog. A marketing part below the minimum warns and never
 * blocks; a missing contact name or phone is the one hard block (BR-LSI6-01).
 */

import { useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { formatUsd } from '../../../../api/billing';
import type { ListingDetailResponse } from '../../../../lib/leasing.api';
import { ListingPublishDialog } from '../../../../components/leasing/ListingPublishDialog';
import { putGoLiveHandoff } from '../../listing-detail/goLiveHandoff';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import { useBillingSubscription } from '../../../../hooks/useBilling';
import { missingMinimumPhrase } from '../../../../lib/marketingCompleteness';
import { guideStepLabel, type GuideStepKey, type GuideStepProps, type GuideStepStatus } from '../guide-steps';
import type { GuideState } from '../useGuideState';

const CONTACT_INFO_REQUIRED_MESSAGE = 'Add a contact name and phone before publishing. Nothing has been charged.';

/** The optional Screening row reads "Optional" until it is set, the same word the preview's panel uses. */
const OPTIONAL_BADGE = { label: 'Optional', variant: 'secondary' } as const;

const STATE_BADGE: Record<GuideStepStatus, { label: string; variant: 'success' | 'warning' | 'secondary' | 'primary' }> = {
  done: { label: 'Done', variant: 'success' },
  needs_attention: { label: 'Needs attention', variant: 'warning' },
  todo: { label: 'Not started', variant: 'secondary' },
  current: { label: 'In progress', variant: 'primary' },
};

/** A row with anything saved offers "Edit"; one with nothing saved yet offers "Set up". */
function hasSavedContent(
  key: ReviewRowKey,
  status: GuideStepStatus,
  guide: GuideState,
  listing: NonNullable<GuideStepProps['listing']>
): boolean {
  if (key === 'marketing') return guide.marketing.partsDone > 0;
  if (key === 'contact') return !!listing.contactName?.trim() || !!listing.contactPhone?.trim();
  return status === 'done';
}

type ReviewRowKey = Exclude<GuideStepKey, 'preview' | 'review'> | 'screening';
const REVIEW_ROWS: readonly ReviewRowKey[] = ['marketing', 'details', 'showings', 'contact', 'agent', 'screening'];

interface ReviewStepProps extends GuideStepProps {
  guide: GuideState;
  stepPath: (key: GuideStepKey) => string;
  /** "Unit 2B" — used in dialog sentences. */
  unitName: string;
  /** "Unit 2B · 412 Oak Street". */
  unitLabel: string | null;
  /** The guide's Back and "Continue to payment" bar; it sits inside the "What happens next" card. */
  actionBar: ReactNode;
}

export function ReviewStep({ listing, guide, stepPath, unitName, unitLabel, actionBar, handleRef }: ReviewStepProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: subscription, isPending: feePending } = useBillingSubscription();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  // Counts blocked attempts, so the alert is brought into view on every one, not only the first.
  const [blockedAttempts, setBlockedAttempts] = useState(0);
  const contactAlertRef = useRef<HTMLDivElement>(null);

  const contactSaved = listing != null && !!listing.contactName?.trim() && !!listing.contactPhone?.trim();

  // "Continue to payment": the contact guard first (no payment UI without a name and phone).
  useImperativeHandle(
    handleRef,
    () => ({
      isDirty: () => false,
      save: async () => {
        if (!contactSaved) {
          setContactError(CONTACT_INFO_REQUIRED_MESSAGE);
          setBlockedAttempts((count) => count + 1);
          return false;
        }
        setContactError(null);
        setPaymentOpen(true);
        return false;
      },
    }),
    [contactSaved]
  );

  // The guard's alert renders at the top while the button is at the bottom: move focus to it (which
  // also scrolls it into view) so a keyboard or screen-reader user, and a phone, land on the reason.
  useEffect(() => {
    if (blockedAttempts === 0) return;
    contactAlertRef.current?.focus();
    contactAlertRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [blockedAttempts]);

  if (!listing) return null;

  const invalidateListing = () => {
    void queryClient.invalidateQueries({ queryKey: ['listing', listing.id] });
    void queryClient.invalidateQueries({ queryKey: ['listings'] });
  };

  const rows = REVIEW_ROWS.map((key) => {
    if (key === 'screening') {
      return {
        key,
        label: 'Screening criteria',
        optional: true,
        hasContent: guide.screening.status === 'done',
        ...guide.screening,
        to: stepPath('contact'),
      };
    }
    const step = guide.steps[key];
    return {
      key,
      label: guideStepLabel(key),
      optional: false,
      hasContent: hasSavedContent(key, step.status, guide, listing),
      ...step,
      to: stepPath(key),
    };
  });
  const belowMinimum = !guide.marketing.minimumMet && !guide.steps.marketing.loading;

  return (
    <div className="flex flex-col gap-6" data-testid="guide-step-review">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Review and publish</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          One last look. Nothing goes live and nothing is charged until you confirm on the next screen.
        </p>
      </div>

      {(contactError || !contactSaved) && (
        <div
          ref={contactAlertRef}
          tabIndex={-1}
          role="alert"
          data-testid="guide-review-contact-required"
          className="rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm text-warning focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <p>{contactError ?? 'Add a contact name and phone. They are required to publish.'}</p>
          <Button asChild variant="link" className="px-0 text-warning">
            <Link to={stepPath('contact')} data-testid="guide-review-contact-link">
              Add contact info
            </Link>
          </Button>
        </div>
      )}

      {belowMinimum && (
        <div
          data-testid="guide-review-marketing-warning"
          className="rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm text-warning"
        >
          Add {missingMinimumPhrase(guide.marketing.missing)} to reach the minimum. You can still publish, but prospects
          will see less.
        </div>
      )}

      <section aria-labelledby="guide-review-live-title" className="rounded-xl border border-border bg-card p-[var(--card-padding)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="guide-review-live-title" className="text-lg font-semibold text-foreground">
            What goes live
          </h2>
          <Button asChild variant="outline">
            <Link to={`/leasing/listings/${listing.id}/preview`} data-testid="guide-review-preview">
              <Eye aria-hidden="true" />
              Preview listing
            </Link>
          </Button>
        </div>
        <ul className="mt-2 flex flex-col" aria-label="What goes live">
          {rows.map((row) => {
            const badge =
              row.optional && row.status !== 'done'
                ? OPTIONAL_BADGE
                : STATE_BADGE[row.status === 'current' ? 'todo' : row.status];
            const editVerb = row.hasContent ? 'Edit' : 'Set up';
            return (
              <li
                key={row.key}
                data-testid={`guide-review-row-${row.key}`}
                data-status={row.loading ? 'loading' : row.status}
                aria-busy={row.loading}
                className="flex flex-wrap items-start gap-x-3 gap-y-1 border-t border-border py-3 first:border-t-0"
              >
                {row.loading ? (
                  <Skeleton aria-hidden="true" className="h-5 w-20 rounded-full" />
                ) : (
                  <Badge variant={badge.variant} data-testid={`guide-review-row-${row.key}-state`} className="shrink-0">
                    {badge.label}
                  </Badge>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">{row.label}</p>
                  {row.loading ? (
                    <Skeleton aria-hidden="true" className="mt-1 h-4 w-3/4 max-w-sm" />
                  ) : (
                    <p className="text-sm text-muted-foreground break-words">{row.summary}</p>
                  )}
                </div>
                <Button asChild variant="link" className="shrink-0 px-2">
                  <Link
                    to={row.to}
                    data-testid={`guide-review-row-${row.key}-edit`}
                    aria-label={`${editVerb} ${row.label.toLowerCase()}`}
                  >
                    {editVerb}
                  </Link>
                </Button>
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-labelledby="guide-review-next-title" className="rounded-xl border border-border bg-card p-[var(--card-padding)]">
        <h2 id="guide-review-next-title" className="text-lg font-semibold text-foreground">
          What happens next
        </h2>
        {feePending ? (
          <div className="mt-2" data-testid="guide-review-fee-loading" aria-busy="true">
            <Skeleton aria-hidden="true" className="h-5 w-full max-w-md" />
          </div>
        ) : (
          <p className="mt-2 text-sm text-foreground" data-testid="guide-review-fee">
            {listing.paidPeriod ? (
              // The unit already has a current paid period (BR-92): no fee is quoted, none is charged.
              'This unit is already paid for, so there is no new charge. Next you confirm and publish.'
            ) : (
              <>
                {subscription?.listingFeeCents != null
                  ? `${formatUsd(subscription.listingFeeCents)} listing fee. `
                  : 'The listing fee is shown on the next screen. '}
                Next you add a card. Nothing is charged until you confirm.
              </>
            )}
          </p>
        )}
        {actionBar}
      </section>

      <ListingPublishDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        listing={listing}
        unitName={unitName}
        unitLabel={unitLabel}
        onCompleted={(message, published) => {
          setPaymentOpen(false);
          // Seed the published listing before leaving: the detail page must never mount on the stale draft,
          // which would bounce into the guide. The go-live message travels in the query cache, not router state (see goLiveHandoff.ts).
          queryClient.setQueryData<ListingDetailResponse>(['listing', listing.id], (previous) =>
            previous ? { ...previous, listing: published } : previous
          );
          invalidateListing();
          putGoLiveHandoff(queryClient, listing.id, message);
          navigate(`/leasing/listings/${listing.id}`, { replace: true });
        }}
        onContactInfoRequired={() => {
          setPaymentOpen(false);
          setContactError(CONTACT_INFO_REQUIRED_MESSAGE);
          setBlockedAttempts((count) => count + 1);
        }}
        onPaidNotPublished={invalidateListing}
      />
    </div>
  );
}

